/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { ApiRootResource } from '@common/api-root-shared';
import { BinaryFileListResource } from '@common/binary-file-shared';
import { CommandOrder, CommandResource } from '@alfa-client/command-shared';
import { StateResource } from '@common/tech-shared';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ResourceUri } from '@ngxp/rest';
import { Observable } from 'rxjs';
import { VorgangWithEingangLinkRel } from '../vorgang.linkrel';
import {
  AdditionalActions,
  VorgangFilter,
  VorgangListResource,
  VorgangResource,
  VorgangStatistic,
  VorgangView,
  VorgangWithEingangResource,
} from '../vorgang.model';
import { VorgangActions } from './vorgang.actions';
import { VorgangSelectors } from './vorgang.selectors';

@Injectable()
export class VorgangFacade {
  private readonly store: Store = inject(Store);

  //VorgangList
  public getVorgangList(): Observable<StateResource<VorgangListResource>> {
    return this.store.select(VorgangSelectors.vorgangList);
  }

  public loadVorgangList(apiRoot: ApiRootResource, linkRel: string): void {
    this.store.dispatch(VorgangActions.loadVorgangList({ apiRoot, linkRel }));
  }

  public getVorgangStatistic(): Observable<StateResource<VorgangStatistic>> {
    return this.store.select(VorgangSelectors.vorgangStatistic);
  }

  public getVorgaenge(): Observable<VorgangResource[]> {
    return this.store.select(VorgangSelectors.vorgaenge);
  }

  public loadNextPage(): void {
    this.store.dispatch(VorgangActions.loadNextPage());
  }

  public getVorgangFilter(): Observable<VorgangFilter> {
    return this.store.select(VorgangSelectors.vorgangFilter);
  }

  public isVorgangFilterSelected(vorgangFilter: VorgangFilter): Observable<boolean> {
    return this.store.select(VorgangSelectors.isVorgangFilterSelected(vorgangFilter));
  }

  public getVorgangView(): Observable<VorgangView> {
    return this.store.select(VorgangSelectors.vorgangView);
  }

  public isVorgangViewSelected(vorgangView: VorgangView): Observable<boolean> {
    return this.store.select(VorgangSelectors.isVorgangViewSelected(vorgangView));
  }

  public getVorgangViewRoutePath(vorgangView: VorgangView): Observable<string> {
    return this.store.select(VorgangSelectors.getVorgangViewRoutePath(vorgangView));
  }

  public getBackButtonUrl(): Observable<string> {
    return this.store.select(VorgangSelectors.backButtonUrl);
  }

  //Search
  public searchVorgaengeBy(apiRoot: ApiRootResource, searchString: string, linkRel: string): void {
    this.store.dispatch(VorgangActions.searchVorgaengeBy({ apiRoot, searchString, linkRel }));
  }

  public searchForPreview(searchString: string): void {
    this.store.dispatch(VorgangActions.searchForPreview({ string: searchString }));
  }

  public setSearchString(searchString: string): void {
    this.store.dispatch(VorgangActions.setSearchString({ string: searchString }));
  }

  public getSearchString(): Observable<string> {
    return this.store.select(VorgangSelectors.searchString);
  }

  public getSearchPreviewList(): Observable<StateResource<VorgangListResource>> {
    return this.store.select(VorgangSelectors.searchPreviewList);
  }

  public clearSearchPreviewList(): void {
    this.store.dispatch(VorgangActions.clearSearchPreviewList());
  }

  public clearSearchString(): void {
    this.store.dispatch(VorgangActions.clearSearchString());
  }

  //VorgangWithEingang
  public getVorgangWithEingang(): Observable<StateResource<VorgangWithEingangResource>> {
    return this.store.select(VorgangSelectors.vorgangWithEingang);
  }

  public loadVorgangWithEingang(vorgangWithEingangUri: ResourceUri): void {
    this.store.dispatch(VorgangActions.loadVorgangWithEingang({ resourceUri: vorgangWithEingangUri }));
  }

  public loadVorgangWithEingangWithAdditionalActions(vorgangWithEingangUri: ResourceUri, additionalActions: AdditionalActions) {
    this.store.dispatch(
      VorgangActions.loadVorgangWithEingang({
        resourceUri: vorgangWithEingangUri,
        additionalActions,
      }),
    );
  }

  public getAttachmentList(): Observable<StateResource<BinaryFileListResource>> {
    return this.store.select(VorgangSelectors.attachmentList);
  }

  public loadAttachmentList(vorgangWithEingang: VorgangWithEingangResource): void {
    this.store.dispatch(VorgangActions.loadAttachmentList({ vorgangWithEingang }));
  }

  public getRepresentationList(): Observable<StateResource<BinaryFileListResource>> {
    return this.store.select(VorgangSelectors.representationList);
  }

  public loadRepresentationList(vorgangWithEingang: VorgangWithEingangResource): void {
    this.store.dispatch(VorgangActions.loadRepresentationList({ vorgangWithEingang }));
  }

  public clearVorgangWithEingang(): void {
    this.store.dispatch(VorgangActions.clearVorgangWithEingang());
  }

  public getForwardPendingCommand(): Observable<StateResource<CommandResource>> {
    return this.store.select(VorgangSelectors.forwardPendingCommand);
  }
  public setForwardSingleCommandLoading(): void {
    this.store.dispatch(VorgangActions.setForwardingSingleCommandLoading());
  }
  public setForwardSingleCommand(commandStateResource: StateResource<CommandResource>): void {
    this.store.dispatch(VorgangActions.setForwardingSingleCommand({ commandStateResource }));
  }

  public getSendPostfachNachrichtPendingCommand(): Observable<StateResource<CommandResource>> {
    return this.store.select(VorgangSelectors.sendPostfachNachrichtPendingCommand);
  }
  public setPendingSendPostfachMailSingleCommandLoading(): void {
    this.store.dispatch(VorgangActions.setSendPostfachNachrichtSingleCommandLoading());
  }
  public setPendingSendPostfachMailSingleCommand(commandStateResource: StateResource<CommandResource>): void {
    this.store.dispatch(VorgangActions.setSendPostfachNachrichtSingleCommand({ commandStateResource }));
  }

  public getAssignUserCommand(): Observable<StateResource<CommandResource>> {
    return this.store.select(VorgangSelectors.assignUserCommand);
  }

  public getUnassignUserCommand(): Observable<StateResource<CommandResource>> {
    return this.store.select(VorgangSelectors.unassignUserCommand);
  }

  public assignUser(userUri: ResourceUri): void {
    this.store.dispatch(VorgangActions.assignUser({ userUri }));
  }

  public unassignUser(): void {
    this.store.dispatch(VorgangActions.unassignUser());
  }

  public getStatusCommand(order: CommandOrder): Observable<StateResource<CommandResource>> {
    return this.store.select(VorgangSelectors.getStatusCommand(order));
  }

  public getRevokeCommand(): Observable<StateResource<CommandResource>> {
    return this.store.select(VorgangSelectors.revokeCommand);
  }

  public getVorgangExport(): Observable<StateResource<boolean>> {
    return this.store.select(VorgangSelectors.vorgangExport);
  }

  public exportVorgang(vorgangWithEingang: VorgangWithEingangResource, linkRel: VorgangWithEingangLinkRel): void {
    this.store.dispatch(VorgangActions.exportVorgang({ vorgangWithEingang, linkRel }));
  }
}
